/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Grid } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Trans } from 'react-i18next';
import Tooltip from '../../common/Tooltip';
import { IntegrationResourceProps } from '../../../redux/enums/IntegrationResourceProps';
import AutocompleteField from './AutocompleteField';
import { getFormKeyBySchemaId } from '../rjsf/utils';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import useAlertDialog from '../../../redux/features/alertDialog/useAlertDialog';

const LABEL_KEY = 'name';

const useStyles = makeStyles(theme => ({
  autocompleteContainer: {
    [theme.breakpoints.down('md')]: {
      width: '16rem',
    },
    [theme.breakpoints.up('md')]: {
      width: '27rem',
    },
    backgroundColor: theme.palette.inverted.dark,
  },
}));

async function clearFields(registry, currentSchemaKey, skipReplaceFields) {
  const formData = await registry.getFormData(`${currentSchemaKey}`);
  await clearTreeFields(formData, registry, skipReplaceFields, currentSchemaKey);
}

async function clearTreeFields(formData, registry, skipReplaceFields, currentSchemaKey) {
  const keys = Object.keys(formData);
  for (const k of keys) {
    if (typeof formData[k] === 'object') {
      await clearTreeFields(formData[k], registry, skipReplaceFields, `${currentSchemaKey}.${k}`);
    } else if (formData[k] && !skipReplaceFields.includes(k)) {
      registry.setFormData(undefined, `${currentSchemaKey}.${k}`);
    }
  }
}

const FormLookupAutocompleteField = (props) => {
  const { registry, idSchema, uiSchema } = props;
  const currentSchemaKey = getFormKeyBySchemaId(idSchema.$id, 2);

  const classes = useStyles();

  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const { openAlertDialog } = useAlertDialog();

  const configField = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE];
  const skipReplaceFields = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_SKIP] ?? [];
  const configuration = IntegrationResourceProps.configurations.find(c => c.name === configField);
  const formDataCategory = registry.getFormData(`${currentSchemaKey}.${configuration.category}`);
  const category = (
    formDataCategory ||
    uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_CATEGORY] ||
    'category'
  ).toLowerCase();
  const tooltip = uiSchema[uiSchemaProperties.UI_TOOLTIP];

  const updateCurrent = (data) => {
    setInputValue(data);
  };

  const onChangedSectionAccepted = (selectedEntry) => {
    openAlertDialog({
      updatedTexts: {
        title: 'autocomplete.modal.title',
        message: 'autocomplete.modal.message',
        confirmButton: 'autocomplete.modal.confirm',
        cancelButton: 'actions.cancel',
      },
      updatedSettings: {
        showCancelButton: true,
      },
      acceptHandler: {
        func: onConfirmReplace,
        args: selectedEntry,
      },
    });
  };

  const updateCurrentValue = (selectedEntry) => {
    if (!value) {
      setValue(selectedEntry);
    } else {
      onChangedSectionAccepted(selectedEntry);
    }
  };

  const onConfirmReplace = async (selectedEntry) => {
    await clearFields(registry, currentSchemaKey, skipReplaceFields);
    setValue(selectedEntry);
  };

  const onChoiceSelected = async (details) => {
    if (details) {
      const keys = Object.keys(details);
      for (const k of keys) {
        if (details[k]) {
          await registry.setFormData(details[k], `${currentSchemaKey}.${k}`);
        }
      }
    }
  };

  return (
    <>
      <Grid container direction="row" alignItems="center" className={classes.autocompleteContainer}>
        <AutocompleteField
          configuration={configuration}
          category={category}
          value={value}
          inputValue={inputValue}
          updateCurrentInput={updateCurrent}
          updateCurrentValue={updateCurrentValue}
          onChoiceSelected={onChoiceSelected}
          labelKey={LABEL_KEY}
          {...props}
        >
          {tooltip && <Tooltip title={<Trans i18nKey={tooltip} />} />}
        </AutocompleteField>
      </Grid>
    </>
  );
};

export default FormLookupAutocompleteField;
